import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InviteUserSchema, type InviteUserInput, type UserDto } from '@cutlyft/zod-schemas';
import { useAuthStore } from '@/stores/auth.store';
import { useUsersList } from '@/hooks/users/use-users-list';
import { useInviteUser } from '@/hooks/users/use-invite-user';
import { useUpdateUser } from '@/hooks/users/use-update-user';
import { useDeactivateUser } from '@/hooks/users/use-deactivate-user';
import { useReactivateUser } from '@/hooks/users/use-reactivate-user';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Plus, Shield, UserX, UserCheck, ShieldAlert } from 'lucide-react';

export function TeamManagement() {
  const currentUser = useAuthStore((state) => state.user);
  const [includeDeactivated, setIncludeDeactivated] = React.useState(true);
  const [isInviteOpen, setIsInviteOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<UserDto | null>(null);

  const { data: usersResponse, isLoading } = useUsersList({
    includeDeactivated,
  });

  const inviteUserMutation = useInviteUser();
  const updateUserMutation = useUpdateUser();
  const deactivateUserMutation = useDeactivateUser();
  const reactivateUserMutation = useReactivateUser();
  const { toast } = useToast();

  const isAuthorized = React.useMemo(() => {
    return currentUser?.roleCode === 'OWNER' || currentUser?.roleCode === 'ADMIN';
  }, [currentUser]);

  const inviteForm = useForm<InviteUserInput>({
    resolver: zodResolver(InviteUserSchema),
    defaultValues: {
      email: '',
      roleCode: 'ESTIMATOR',
    },
  });

  const onInviteSubmit = (values: InviteUserInput) => {
    inviteUserMutation.mutate(values, {
      onSuccess: (data) => {
        toast({
          title: 'Invitation Sent',
          description: `An invitation email has been sent to ${values.email}.`,
        });
        setIsInviteOpen(false);
        inviteForm.reset();
        
        // In dev environment, helpfully display the acceptToken since SMTP SES is mocked
        if (data.acceptToken) {
          console.log(`[Dev Mode] Accept invitation token: ${data.acceptToken}`);
          toast({
            title: '[Dev Link] Invitation Token',
            description: `Token: ${data.acceptToken}. Use this to accept the invite via verify page. Check console logs.`,
            duration: 10000,
          });
        }
      },
      onError: (err: any) => {
        const code = err.response?.data?.error?.code;
        const message =
          code === 'CONFLICT'
            ? 'This email id cannot be registered.'
            : err.response?.data?.error?.message || 'Failed to send invitation.';
        toast({
          variant: 'destructive',
          title: 'Invitation Failed',
          description: message,
        });
      },
    });
  };

  const handleRoleChange = (userId: number, newRole: any) => {
    updateUserMutation.mutate(
      {
        id: userId,
        input: { roleCode: newRole },
      },
      {
        onSuccess: () => {
          toast({
            title: 'Role Updated',
            description: 'User role has been changed successfully.',
          });
          setEditingUser(null);
        },
        onError: (err: any) => {
          toast({
            variant: 'destructive',
            title: 'Update Failed',
            description: err.response?.data?.error?.message || 'Failed to change role.',
          });
        },
      }
    );
  };

  const handleDeactivate = (userId: number) => {
    deactivateUserMutation.mutate(userId, {
      onSuccess: () => {
        toast({
          title: 'User Deactivated',
          description: 'The user has been deactivated and can no longer log in.',
        });
      },
      onError: (err: any) => {
        toast({
          variant: 'destructive',
          title: 'Action Failed',
          description: err.response?.data?.error?.message || 'Failed to deactivate user.',
        });
      },
    });
  };

  const handleReactivate = (userId: number) => {
    reactivateUserMutation.mutate(userId, {
      onSuccess: () => {
        toast({
          title: 'User Reactivated',
          description: 'The user has been successfully reactivated.',
        });
      },
      onError: (err: any) => {
        toast({
          variant: 'destructive',
          title: 'Action Failed',
          description: err.response?.data?.error?.message || 'Failed to reactivate user.',
        });
      },
    });
  };

  const getRoleBadgeStyle = (role: string) => {
    switch (role) {
      case 'OWNER':
        return 'bg-primary/15 text-primary border border-primary/30';
      case 'ADMIN':
        return 'bg-info/15 text-info border border-info/30';
      case 'ESTIMATOR':
        return 'bg-success/15 text-success border border-success/30';
      case 'PRODUCTION':
        return 'bg-warning/15 text-warning border border-warning/30';
      case 'SALES':
        return 'bg-accent/20 text-accent border border-accent/40';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  };

  const getUserInitials = (user: UserDto) => {
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || user.email.charAt(0).toUpperCase();
  };

  const getUserFullName = (user: UserDto) => {
    if (!user.firstName && !user.lastName) return 'Pending Invitation';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  };

  if (isLoading) {
    return (
      <Card className="border border-border/50 bg-bg-elevated/80 shadow-md">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-10 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-border/50 bg-bg-elevated/80 shadow-md">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-xl text-fg-primary">Team Management</CardTitle>
          <CardDescription className="text-fg-secondary">
            Manage your fabricator's team members, invite estimaters and production operators, and control roles.
          </CardDescription>
        </div>

        {isAuthorized && (
          <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/95 text-primary-foreground font-semibold flex items-center gap-1.5 h-10">
                <Plus className="h-4 w-4" />
                <span>Invite Member</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
                <DialogDescription>
                  Send an email invitation. The user will be requested to set their password.
                </DialogDescription>
              </DialogHeader>

              <Form {...inviteForm}>
                <form onSubmit={inviteForm.handleSubmit(onInviteSubmit)} className="space-y-4 pt-2">
                  <FormField
                    control={inviteForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input placeholder="member@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={inviteForm.control}
                    name="roleCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Team Member Role</FormLabel>
                        <Combobox
                          options={[
                            { value: 'ADMIN', label: 'Administrator' },
                            { value: 'ESTIMATOR', label: 'Estimator' },
                            { value: 'PRODUCTION', label: 'Production Operator' },
                            { value: 'SALES', label: 'Sales Representative' },
                            { value: 'VIEWER', label: 'Viewer (Read-only)' },
                          ]}
                          value={field.value}
                          onValueChange={(v) => field.onChange(v ?? field.value)}
                          placeholder="Select role"
                          searchPlaceholder="Search role..."
                          clearable={false}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <DialogFooter className="pt-4">
                    <Button
                      type="submit"
                      className="w-full bg-primary text-primary-foreground font-semibold"
                      disabled={inviteUserMutation.isPending}
                    >
                      {inviteUserMutation.isPending ? 'Sending...' : 'Send Invitation'}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Toggle Filter */}
        <div className="flex items-center justify-between pb-2 border-b border-border/50">
          <span className="text-xs text-fg-secondary">
            Showing {usersResponse?.data?.length || 0} workspace members
          </span>
          {isAuthorized && (
            <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-fg-secondary">
              <input
                type="checkbox"
                checked={includeDeactivated}
                onChange={(e) => setIncludeDeactivated(e.target.checked)}
                className="rounded border-border focus:ring-primary h-3.5 w-3.5 text-primary"
              />
              <span>Include deactivated members</span>
            </label>
          )}
        </div>

        {/* Members List */}
        <div className="space-y-3">
          {usersResponse?.data?.length === 0 ? (
            <div className="text-center py-8 text-sm text-fg-muted">No team members found.</div>
          ) : (
            usersResponse?.data?.map((u) => (
              <div
                key={u.id}
                className={`flex items-center justify-between p-3.5 rounded-lg border border-border/30 transition-all ${
                  u.isDeactivated ? 'bg-bg-subtle/40 opacity-70' : 'bg-bg-elevated/40'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                      {getUserInitials(u)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-fg-primary">
                        {getUserFullName(u)}
                      </span>
                      {u.id === currentUser?.id && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-primary/30 text-primary">
                          You
                        </Badge>
                      )}
                      {u.isDeactivated && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-error/30 text-error">
                          Deactivated
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-fg-secondary">{u.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {/* Member Role Badge */}
                  <Badge className={`font-semibold capitalize ${getRoleBadgeStyle(u.roleCode)}`}>
                    {u.roleCode.toLowerCase()}
                  </Badge>

                  {/* Dropdown Menu actions for OWNER/ADMIN on other users */}
                  {isAuthorized && u.id !== currentUser?.id && u.roleCode !== 'OWNER' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-secondary">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {/* Edit Role Option */}
                        <DropdownMenuItem onClick={() => setEditingUser(u)} className="cursor-pointer">
                          <Shield className="mr-2 h-4 w-4" />
                          <span>Change Role</span>
                        </DropdownMenuItem>

                        {/* Deactivate/Reactivate Option */}
                        {u.isDeactivated ? (
                          <DropdownMenuItem
                            onClick={() => handleReactivate(u.id)}
                            className="cursor-pointer text-success focus:bg-success-bg focus:text-success"
                          >
                            <UserCheck className="mr-2 h-4 w-4" />
                            <span>Reactivate</span>
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handleDeactivate(u.id)}
                            className="cursor-pointer text-error focus:bg-error-bg focus:text-error"
                          >
                            <UserX className="mr-2 h-4 w-4" />
                            <span>Deactivate</span>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Change Role Dialog */}
        {editingUser && (
          <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
            <DialogContent className="sm:max-w-[400px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-primary" />
                  Change User Role
                </DialogTitle>
                <DialogDescription>
                  Modify the permissions level for <strong>{getUserFullName(editingUser)}</strong> ({editingUser.email}).
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-3">
                <Label>Select New Role</Label>
                <Combobox
                  options={[
                    { value: 'ADMIN', label: 'Administrator (Full Access)' },
                    { value: 'ESTIMATOR', label: 'Estimator (Project Calculations)' },
                    { value: 'PRODUCTION', label: 'Production Operator (View + Offcuts)' },
                    { value: 'SALES', label: 'Sales Representative (Create + View)' },
                    { value: 'VIEWER', label: 'Viewer (Read-only)' },
                  ]}
                  value={editingUser.roleCode}
                  onValueChange={(v) => v && handleRoleChange(editingUser.id, v)}
                  placeholder="Select role"
                  searchPlaceholder="Search role..."
                  clearable={false}
                />
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditingUser(null)}>
                  Cancel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}
